import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaLinkedin, FaTiktok } from 'react-icons/fa';

const Contact = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get('/settings');
      setSettings(data);
    } catch (error) {
      console.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      facebook: FaFacebook,
      instagram: FaInstagram,
      twitter: FaTwitter,
      youtube: FaYoutube,
      linkedin: FaLinkedin,
      tiktok: FaTiktok
    };
    return icons[platform] || null;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-wide text-center mb-4">
          {t('contact.title')}
        </h1>
        <p className="text-center text-gray-600 font-light tracking-wide mb-16">
          {t('contact.subtitle')}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Info */}
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-gray-50 p-8">
              <h2 className="font-serif text-2xl font-light tracking-wide text-gray-900 mb-8">{t('contact.info.title')}</h2>

              <div className="space-y-6">
                {settings?.contactEmail && (
                  <div className="flex items-start gap-4">
                    <FiMail className="text-gray-400 text-xl mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-light text-gray-900 mb-1">{t('contact.info.email')}</p>
                      <a href={`mailto:${settings.contactEmail}`} className="text-gray-600 hover:text-gray-900 transition font-light">
                        {settings.contactEmail}
                      </a>
                    </div>
                  </div>
                )}

                {settings?.contactPhone && (
                  <div className="flex items-start gap-4">
                    <FiPhone className="text-gray-400 text-xl mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-light text-gray-900 mb-1">{t('contact.info.phone')}</p>
                      <a href={`tel:${settings.contactPhone}`} className="text-gray-600 hover:text-gray-900 transition font-light">
                        {settings.contactPhone}
                      </a>
                    </div>
                  </div>
                )}

                {settings?.contactAddress && (
                  <div className="flex items-start gap-4">
                    <FiMapPin className="text-gray-400 text-xl mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-light text-gray-900 mb-1">{t('contact.info.address')}</p>
                      <p className="text-gray-600 whitespace-pre-wrap font-light">{settings.contactAddress}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Social Media */}
            {settings?.socialMedia && Object.values(settings.socialMedia).some(url => url) && (
              <div className="bg-gray-50 p-8">
                <h2 className="font-serif text-2xl font-light tracking-wide text-gray-900 mb-8">{t('contact.social.title')}</h2>
                <div className="flex gap-4 flex-wrap">
                  {Object.entries(settings.socialMedia).map(([platform, url]) => {
                    if (!url) return null;
                    const Icon = getSocialIcon(platform);
                    if (!Icon) return null;

                    return (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 flex items-center justify-center border border-gray-200 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition text-xl"
                        title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                      >
                        <Icon />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="bg-gray-50 p-8">
            <h2 className="font-serif text-2xl font-light tracking-wide text-gray-900 mb-8">{t('contact.location.title')}</h2>
            {settings?.contactMapUrl ? (
              <div className="relative w-full h-96 overflow-hidden">
                <iframe
                  src={settings.contactMapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0"
                ></iframe>
              </div>
            ) : (
              <div className="w-full h-96 bg-white flex items-center justify-center text-gray-500 font-light">
                {t('contact.location.noMap')}
              </div>
            )}
          </div>
        </div>

        {/* Working Hours */}
        <div className="bg-gray-50 p-8">
          <h2 className="font-serif text-2xl font-light tracking-wide text-gray-900 mb-8 text-center">
            {t('contact.hours.title')}
          </h2>
          <div className="max-w-2xl mx-auto space-y-3">
            <div className="flex justify-between py-3 border-b border-gray-200">
              <span className="font-light text-gray-900">{t('contact.hours.weekdays')}</span>
              <span className="text-gray-600 font-light">{settings?.workingHours?.weekdays || "09:00 - 18:00"}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-200">
              <span className="font-light text-gray-900">{t('contact.hours.saturday')}</span>
              <span className="text-gray-600 font-light">{settings?.workingHours?.saturday || "10:00 - 16:00"}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-200">
              <span className="font-light text-gray-900">{t('contact.hours.sunday')}</span>
              <span className="text-gray-600 font-light">{settings?.workingHours?.sunday || t('contact.hours.closed')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;

